'use client';

import { useEffect, useState } from 'react';

import './styles.css';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { BlockReference } from '@/cms/types/block';
import { BlockComponentModel } from '@/cms/types/blok-component';
import { ImageStrapi as IImageStrapi } from '@/cms/types/image';
import { env } from '@/constant/process';

import Button from '@/components/atoms/Button';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { PopoverProps } from '@/components/atoms/Popover';
import SocialWidgetItem from '@/components/organisms/SocialWidget/SocialWidgetItem';

import { LiveChatWidget } from './LiveChatWidget';

import closeSrc from '~/icons/x-close-02.svg';

import { SvgIcon } from '@/components/SvgIcon';
import {
  LIVECHAT_CONTAINER_ID,
  LIVECHAT_MINIMIZED_ID,
} from '@/components/organisms/SocialWidget/consts';

const isChinaLocale = env.publicLocale === 'zh-cn' && !env.isVisualTest;
export type SocialWidgetEntry = {
  icon: ImageStrapiProps;
  link: LinkButtonStrapiProps;
  popup?: ImageStrapiProps | ImageStrapiProps[];
};

type Position = 'right' | 'left';
export type SocialWidgetStrapiProps = {
  unfolded: boolean;
  position: Position;
  items: SocialWidgetEntry[];
  actionFloatingButton: ImageStrapiProps | BlockReference<IImageStrapi>;
};

const getImageLinkTrackerContext = (
  context: string,
  url: string | undefined,
  index: number
) => {
  let result = `${context}-image-link-`;

  if (!url) {
    result += index;
    return result;
  }

  if (url.startsWith('mailto:')) {
    result += url.replace('mailto:', '').split('@')[0];
    return result;
  }

  try {
    const domain = new URL(url).hostname;
    result += domain;

    return result;
  } catch (e) {
    console.error('Error parsing URL', e);
    return (result += 'unknown');
  }
};

const getOffsetForPosition = (position: Position): `${Position}-${number}` =>
  position === 'left' ? 'left-10' : 'right-10';

export const SocialWidgetStrapi = (
  props: BlockComponentModel<SocialWidgetStrapiProps>
) => {
  const { actionFloatingButton, items, position, unfolded } = props.block;

  const [isUnfolded, setIsUnfolded] = useState<boolean>(unfolded ?? false);
  const [liveChatReady, setLiveChatReady] = useState(isChinaLocale);
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const { texts } = useTranslation();

  useEffect(() => {
    if (!liveChatReady) return;
    setLiveChatVisibile(props.block.unfolded);
  }, [unfolded, liveChatReady, props.block.unfolded]);

  const setLiveChatVisibile = (val: boolean) => {
    const container = document.querySelector(
      `#${LIVECHAT_CONTAINER_ID}`
    ) as HTMLDivElement | null;

    if (!container) {
      return;
    }

    if (val) {
      container.style.visibility = 'visible';
      container.style.opacity = '1';
    } else {
      container.style.visibility = 'hidden';
      container.style.opacity = '0';
    }
  };

  const handleLiveChatReady = () => {
    setLiveChatReady(true);
    const container = document.querySelector(`#${LIVECHAT_CONTAINER_ID}`);
    const minimizedContainer = document.querySelector(
      `#${LIVECHAT_MINIMIZED_ID}`
    );

    if (container)
      container.classList.add(
        'position-hack-for-live-chat-widget',
        `position-hack-for-live-chat-widget-${props.block.position}`
      );

    if (minimizedContainer)
      minimizedContainer.classList.add(
        'position-hack-for-live-chat-widget',
        `position-hack-for-live-chat-widget-${props.block.position}`
      );

    setLiveChatVisibile(isUnfolded);
  };

  const toggleUnfolded = () => {
    setLiveChatVisibile(!isUnfolded);
    setIsUnfolded((prev) => !prev);
    setActiveTabIndex(null);
  };

  const popupProps: Partial<PopoverProps> = {
    position: props.block.position === 'right' ? 'left' : 'right',
  };

  function handleSelect(index: number) {
    index !== activeTabIndex
      ? setActiveTabIndex(index)
      : setActiveTabIndex(null);
  }

  const isLiveChatVisible = liveChatReady && isUnfolded;

  // @TODO: Can be safely removed once AWD-5761 is alive and kicking
  const TEMP__backwardsCompatibleFloatingButtonIcon = (): ImageStrapiProps => {
    const isBlockReference = (block: any): block is BlockReference<any> => {
      return 'data' in block;
    };

    if (isBlockReference(actionFloatingButton)) {
      return { image: actionFloatingButton };
    }

    if (!actionFloatingButton?.image?.data?.attributes)
      return { image: undefined };

    return {
      image: {
        data: { attributes: actionFloatingButton?.image?.data?.attributes },
      },
    };
  };

  const actionButtonImage = TEMP__backwardsCompatibleFloatingButtonIcon();

  return (
    <>
      <div
        className={cn(
          'light',
          'bg-base-white fixed bottom-[180px] z-50 flex flex-col rounded-full p-3',
          getOffsetForPosition(position),
          'shadow-popup',
          isLiveChatVisible ? 'visible' : 'hidden'
        )}
        id='social-button-container'
      >
        {items?.map((item, index) => {
          return (
            <div key={index}>
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border border-gray-300',
                  activeTabIndex !== index && 'hover:border-brand-500',
                  activeTabIndex === index && 'border-brand-500 border-2',
                  index !== 0 && 'mt-2'
                )}
              >
                <SocialWidgetItem
                  icon={item.icon}
                  key={index}
                  link={item.link}
                  handleSelectCallback={() => handleSelect(index)}
                  popup={item.popup}
                  PopupProps={popupProps}
                  data-tracker-context={getImageLinkTrackerContext(
                    'social-widget',
                    item.link?.url,
                    index
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={cn(
          'bg-brand-500 text-base-white text-body-m fixed bottom-[224px] right-[18px] z-20 flex flex-col items-center rounded-r-xl px-1 py-2 shadow-lg',
          isLiveChatVisible ? 'visible' : 'hidden'
        )}
      >
        {texts.social_widget.key_account_manager
          .split('')
          .map((char: string, idx: number) => (
            <span key={idx}>{char}</span>
          ))}
      </div>

      <div
        className={cn(
          'bg-brand-500 text-base-white text-body-m fixed bottom-[120px] right-[108px] z-20 flex flex-col items-center rounded-full px-2 py-1 shadow-lg',
          isLiveChatVisible ? 'visible' : 'hidden'
        )}
      >
        {texts.social_widget.customer_support}
      </div>
      <Button
        onClick={toggleUnfolded}
        className={cn(
          'bg-brand-500 h-15 w-15 fixed bottom-8 z-50 rounded-full border-none',
          'shadow-popup',
          getOffsetForPosition(position)
        )}
      >
        <SvgIcon
          src={closeSrc}
          className={cn(
            'light text-base-white h-10 w-10',
            !isUnfolded && 'hidden'
          )}
        />
        <div className={isUnfolded ? 'hidden' : ''}>
          <ImageStrapi block={actionButtonImage} />
        </div>
      </Button>

      <LiveChatWidget showPlaceholder={false} onReady={handleLiveChatReady} />
    </>
  );
};
export default SocialWidgetStrapi;
